<template>
  <v-card class="pa-4">
    <h2 class="headline mb-2 text-center">
      {{ isNewPatient ? 'Necesitamos algunos datos' : 'Confirma tus datos de contacto' }}
    </h2>
    <p class="body-1 text-center mb-6 grey--text">
      Para reservar tu hora, por favor completa lo siguiente
    </p>

    <v-form v-model="valid" ref="form">
      <!-- Sección: Datos personales (solo paciente nuevo) -->
      <template v-if="isNewPatient">
        <div class="subtitle-1 font-weight-medium mb-2">Datos personales</div>
        <v-text-field
          v-model="form.name"
          label="Nombres"
          outlined
          :rules="[req]"
        ></v-text-field>
        <v-text-field
          v-model="form.fLastName"
          label="Apellido paterno"
          outlined
          :rules="[req]"
        ></v-text-field>
        <v-text-field
          v-model="form.mLastName"
          label="Apellido materno (opcional)"
          outlined
        ></v-text-field>
      </template>

      <!-- Sección: Fecha de nacimiento + género (nuevo o faltante) -->
      <template v-if="needsBirth">
        <v-menu
          v-model="birthMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="form.birthDate"
              label="Fecha de nacimiento"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
              :rules="[req]"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="form.birthDate"
            no-title
            :max="today"
            :active-picker.sync="activePicker"
            @input="birthMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-select
          v-model="form.gender"
          :items="genderOptions"
          label="Género"
          outlined
          :rules="[req]"
        ></v-select>
      </template>

      <!-- Sección: Datos de contacto (SIEMPRE) -->
      <div class="subtitle-1 font-weight-medium mb-2 mt-2">Datos de contacto</div>
      <p class="caption grey--text mb-2">
        Usaremos estos datos para confirmarte y recordarte tu hora
      </p>
      <v-text-field
        v-model="form.phoneNumber"
        label="Teléfono celular"
        outlined
        prefix="+56 9"
        hint="8 dígitos"
        persistent-hint
        :rules="[req, phoneRule]"
      ></v-text-field>
      <v-text-field
        class="mt-2"
        v-model="form.email"
        label="Email"
        outlined
        type="email"
        :rules="[req, emailRule]"
      ></v-text-field>

      <!-- Sección: Previsión (nuevo o faltante) -->
      <template v-if="needsAgreement">
        <div class="subtitle-1 font-weight-medium mb-2 mt-2">Previsión</div>
        <v-select
          v-model="form.agreementType"
          :items="[
            { text: 'Fonasa', value: 'fonasa' },
            { text: 'Particular / Isapre', value: 'particular' }
          ]"
          label="Previsión"
          outlined
          :rules="[req]"
        ></v-select>
      </template>

      <!-- Términos y condiciones -->
      <v-checkbox v-model="form.terms" :rules="[v => !!v || 'Debe aceptar los términos']">
        <template v-slot:label>
          <div>
            Acepto los
            <a href="javascript:;" @click.stop="showTerms = true">términos y condiciones</a>
          </div>
        </template>
      </v-checkbox>
    </v-form>

    <v-btn
      color="primary"
      block
      large
      class="mt-2"
      :loading="saving"
      :disabled="!valid"
      @click="submit"
    >Continuar</v-btn>

    <!-- Popup de compromiso -->
    <v-dialog v-model="showTerms" max-width="520">
      <v-card class="pa-5">
        <h3 class="title mb-3">Compromiso de asistencia</h3>
        <p class="body-2">
          Días antes de tu consulta, te contactaremos por email, teléfono y/o WhatsApp
          para que confirmes o anules tu hora. Al momento de confirmar tu hora, te
          comprometes a venir a la clínica el día y la hora que reservaste.
        </p>
        <p class="body-2">
          Si no puedes venir, es muy importante que anules, porque así le das la
          oportunidad a otra persona de atenderse.
        </p>
        <div class="text-right">
          <v-btn color="primary" text @click="acceptTerms">Entendido</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showError" :timeout="6000" color="error" top>
      {{ errorMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { toApiFormat } from "../utils/rut";

export default {
  name: "CompleteProfileStep",
  props: {
    isNewPatient: { type: Boolean, default: false },
    rut: { type: String, default: "" },
    requiresBirthDate: { type: Boolean, default: false },
    requiresAgreement: { type: Boolean, default: false }
  },
  data: () => ({
    valid: false,
    saving: false,
    birthMenu: false,
    activePicker: null,
    showTerms: false,
    showError: false,
    errorMessage: "",
    form: {
      name: "",
      fLastName: "",
      mLastName: "",
      birthDate: null,
      gender: null,
      phoneNumber: "",
      email: "",
      agreementType: null,
      terms: false
    },
    genderOptions: [
      { text: "Femenino", value: "femenino" },
      { text: "Masculino", value: "masculino" },
      { text: "No binario", value: "no_binario" },
      { text: "Otro", value: "otro" },
      { text: "Prefiero no especificar", value: "no_especificado" }
    ]
  }),
  computed: {
    needsBirth() {
      return this.isNewPatient || this.requiresBirthDate;
    },
    needsAgreement() {
      return this.isNewPatient || this.requiresAgreement;
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    }
  },
  watch: {
    birthMenu(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"));
    }
  },
  methods: {
    req(v) {
      return !!v || "Campo requerido";
    },
    phoneRule(v) {
      const c = (v || "").toString().replace(/\D/g, "");
      return c.length === 8 || "Debe tener 8 dígitos (+56 9 XXXXXXXX)";
    },
    emailRule(v) {
      return /.+@.+\..+/.test(v || "") || "Email inválido";
    },
    acceptTerms() {
      this.form.terms = true;
      this.showTerms = false;
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const phone = "9" + this.form.phoneNumber.toString().replace(/\D/g, "");
        if (this.isNewPatient) {
          await this.$store.dispatch("register", {
            rut: toApiFormat(this.rut),
            name: this.form.name,
            fLastName: this.form.fLastName,
            mLastName: this.form.mLastName,
            birthDate: this.form.birthDate,
            gender: this.form.gender,
            agreementType: this.form.agreementType,
            email: this.form.email,
            phoneNumber: phone,
            termsAccepted: true
          });
          this.$emit("completed", { requiresBirthDate: false, requiresAgreement: false });
        } else {
          let payload = { email: this.form.email, phoneNumber: phone };
          if (this.requiresBirthDate) {
            payload.birthDate = this.form.birthDate;
            payload.gender = this.form.gender;
          }
          if (this.requiresAgreement) {
            payload.agreementType = this.form.agreementType;
          }
          let { data } = await this.$http.post("completeProfile", payload);
          await this.$store.dispatch("setBookingFlags", {
            requiresBirthDate: data.requiresBirthDate,
            requiresAgreement: data.requiresAgreement
          });
          this.$emit("completed", data);
        }
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.msg) ||
          "No fue posible guardar los datos. Intenta nuevamente.";
        this.showError = true;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
</style>
